import { useCallback, useEffect, useState } from "react"
import { Brain, Check, Lock, Pencil, Search, Trash2, X } from "lucide-react"
import { api } from "@/shared/api-client"

interface ScopeInfo { scope: string; counts: Record<string, number>; has_passport: boolean }
interface Overview { scopes: ScopeInfo[]; projects: { id: string; name: string; kind: string }[]; pending_skills: number; config: { mode: string; skills_auto: boolean }; is_admin: boolean }
interface Atom { id: number; scope: string; kind: string; text: string; domain: string; status: string; confidence: number; hits: number; locked: number; updated: number }
interface Scene { id: number; title: string; summary: string; open_threads: string; created: number }

const KIND: Record<string, string> = { fact: "факт", preference: "предпочтение", decision: "решение", event: "событие", skill: "навык" }
const DOMAIN: Record<string, string> = { video: "видео", programming: "программирование", system: "система", home: "дом", trading: "торговля", family: "семья", general: "общее" }

function scopeTitle(s: string, ov: Overview | null): string {
  const [kind, id] = s.split(":")
  if (kind === "user") return id === "admin" ? "Моя память (владелец)" : `Личная память: ${id}`
  if (kind === "project") return "Проект: " + (ov?.projects.find((p) => p.id === id)?.name ?? id)
  if (kind === "family") return "Семья (общее)"
  if (kind === "skills") return "Общие навыки"
  return s
}

export function JarvisMemoryPage() {
  const [ov, setOv] = useState<Overview | null>(null)
  const [scope, setScope] = useState<string>("")
  const [tab, setTab] = useState<"passport" | "atoms" | "skills">("passport")
  const [passport, setPassport] = useState("")
  const [dirty, setDirty] = useState(false)
  const [atoms, setAtoms] = useState<Atom[]>([])
  const [scenes, setScenes] = useState<Scene[]>([])
  const [q, setQ] = useState("")
  const [pending, setPending] = useState<Atom[]>([])
  const [approved, setApproved] = useState<Atom[]>([])
  const [editing, setEditing] = useState<{ id: number; text: string } | null>(null)
  const [msg, setMsg] = useState("")

  const loadOv = useCallback(() => api.get<Overview>("/jarvis-memory/overview").then((o) => {
    setOv(o)
    setScope((cur) => cur || o.scopes.find((s) => s.scope.startsWith("user:"))?.scope || o.scopes[0]?.scope || "")
  }).catch((e) => setMsg(String(e))), [])

  useEffect(() => { void loadOv() }, [loadOv])

  const loadScope = useCallback(async () => {
    if (!scope || scope === "skills") return
    try {
      const p = await api.get<{ text: string }>(`/jarvis-memory/passport?scope=${encodeURIComponent(scope)}`)
      setPassport(p.text); setDirty(false)
      setAtoms(await api.get<Atom[]>(`/jarvis-memory/atoms?scope=${encodeURIComponent(scope)}&q=${encodeURIComponent(q)}`))
      setScenes(await api.get<Scene[]>(`/jarvis-memory/scenes?scope=${encodeURIComponent(scope)}`))
    } catch (e) { setMsg(String(e)) }
  }, [scope, q])

  const loadSkills = useCallback(async () => {
    try {
      setApproved(await api.get<Atom[]>("/jarvis-memory/skills?status=active"))
      if (ov?.is_admin) setPending(await api.get<Atom[]>("/jarvis-memory/skills?status=pending"))
    } catch (e) { setMsg(String(e)) }
  }, [ov?.is_admin])

  useEffect(() => { if (tab !== "skills") void loadScope() }, [loadScope, tab])
  useEffect(() => { if (tab === "skills") void loadSkills() }, [loadSkills, tab])

  const savePassport = async () => {
    await api.put("/jarvis-memory/passport", { scope, text: passport }); setDirty(false); setMsg("Паспорт сохранён")
  }
  const rebuild = async () => {
    setMsg("Обновляю паспорт по фактам…")
    await api.post(`/jarvis-memory/consolidate?scope=${encodeURIComponent(scope)}`, {})
    await loadScope(); setMsg("Паспорт обновлён")
  }
  const patch = async (id: number, body: object) => { await api.patch(`/jarvis-memory/atoms/${id}`, body); await (tab === "skills" ? loadSkills() : loadScope()) }
  const del = async (id: number) => { if (confirm("Удалить из памяти?")) { await api.delete(`/jarvis-memory/atoms/${id}`); await (tab === "skills" ? loadSkills() : loadScope()) } }
  const skillAct = async (id: number, action: "approve" | "reject") => { await api.post(`/jarvis-memory/skills/${id}/${action}`, {}); await loadSkills(); await loadOv() }
  const setAuto = async (v: boolean) => { await api.put("/jarvis-memory/config", { skills_auto: v }); await loadOv() }

  const tabBtn = (id: typeof tab, label: string, badge?: number) => (
    <button key={id} onClick={() => setTab(id)}
      className={`rounded px-3 py-1.5 text-sm font-semibold ${tab === id ? "bg-[#1e3a8a] text-white" : "bg-[#1c2940] text-[#9fb0c8] hover:bg-[#24344f]"}`}>
      {label}{badge ? <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-xs text-black">{badge}</span> : null}
    </button>
  )

  const atomRow = (a: Atom, skill = false) => (
    <li key={a.id} className="rounded border border-[#2a364b] bg-[#151c2b] p-2.5 text-sm">
      {editing?.id === a.id ? (
        <div className="flex flex-col gap-2">
          <textarea value={editing.text} onChange={(e) => setEditing({ id: a.id, text: e.target.value })} rows={3} className="w-full rounded border border-[#2a364b] bg-[#0d1320] p-2 text-[#e8eef8]" />
          <div className="flex gap-2">
            <button onClick={async () => { await patch(a.id, { text: editing.text }); setEditing(null) }} className="rounded bg-emerald-600 px-3 py-1 text-white">Сохранить</button>
            <button onClick={() => setEditing(null)} className="rounded bg-zinc-700 px-3 py-1 text-white">Отмена</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="break-words text-[#e8eef8]">{a.locked ? <Lock size={12} className="mr-1 inline text-amber-400" /> : null}{a.text}</p>
            <p className="mt-1 text-xs text-[#8d9ab0]">
              {KIND[a.kind] ?? a.kind}{a.domain ? ` · ${DOMAIN[a.domain] ?? a.domain}` : ""} · уверенность {Math.round(a.confidence * 100)}% · подтверждений {a.hits} · A{a.id}
            </p>
          </div>
          <div className="flex shrink-0 gap-1.5">
            {skill && a.status === "pending" && ov?.is_admin && (<>
              <button onClick={() => skillAct(a.id, "approve")} title="Одобрить" className="rounded bg-emerald-600 p-1.5 text-white"><Check size={15} /></button>
              <button onClick={() => skillAct(a.id, "reject")} title="Отклонить" className="rounded bg-rose-700 p-1.5 text-white"><X size={15} /></button>
            </>)}
            {!skill && <button onClick={() => patch(a.id, { locked: !a.locked })} title={a.locked ? "Снять закрепление" : "Закрепить навсегда"} className={`rounded p-1.5 ${a.locked ? "bg-amber-500 text-black" : "bg-[#24344f] text-[#9fb0c8]"}`}><Lock size={15} /></button>}
            {(!skill || ov?.is_admin) && <button onClick={() => setEditing({ id: a.id, text: a.text })} title="Править" className="rounded bg-[#24344f] p-1.5 text-[#9fb0c8]"><Pencil size={15} /></button>}
            {(!skill || ov?.is_admin) && <button onClick={() => del(a.id)} title="Удалить" className="rounded bg-[#24344f] p-1.5 text-rose-300"><Trash2 size={15} /></button>}
          </div>
        </div>
      )}
    </li>
  )

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-3 p-3 text-[#e8eef8] sm:p-5">
      <header className="flex items-center gap-2">
        <Brain className="text-[#69d7ff]" />
        <h1 className="text-xl font-black">Память Джарвиса</h1>
      </header>
      <p className="text-sm text-[#8d9ab0]">
        Слои: разговоры → факты → сюжетные блоки → паспорт. Личная память видна только человеку, общие навыки («как делать») помогают всей семье.
      </p>
      {msg && <p className="rounded border border-[#2a364b] bg-[#1c2940] px-3 py-1.5 text-sm text-[#9fb0c8]">{msg}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <select value={scope} onChange={(e) => { setScope(e.target.value); if (tab === "skills") setTab("passport") }}
          className="min-w-0 max-w-full rounded border border-[#2a364b] bg-[#0d1320] px-2 py-1.5 text-sm">
          {ov?.scopes.filter((s) => s.scope !== "skills").map((s) => <option key={s.scope} value={s.scope}>{scopeTitle(s.scope, ov)}</option>)}
        </select>
        {tabBtn("passport", "Паспорт")}
        {tabBtn("atoms", "Факты")}
        {tabBtn("skills", "Общие навыки", ov?.pending_skills)}
      </div>

      {tab === "passport" && (
        <section className="flex flex-col gap-2">
          <textarea value={passport} onChange={(e) => { setPassport(e.target.value); setDirty(true) }} rows={14}
            placeholder="Паспорт пока пуст: он собирается сам из фактов, когда их наберётся достаточно. Можно написать вручную."
            className="w-full rounded border border-[#2a364b] bg-[#0d1320] p-3 text-sm leading-relaxed text-[#e8eef8]" />
          <div className="flex flex-wrap gap-2">
            <button disabled={!dirty} onClick={savePassport} className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">Сохранить</button>
            <button onClick={rebuild} className="rounded bg-[#24344f] px-3 py-1.5 text-sm font-semibold text-[#cfe0f5]">Обновить по фактам</button>
          </div>
          {scenes.length > 0 && (<>
            <h2 className="mt-2 text-sm font-bold text-[#9fb0c8]">Недавние события (сюжетные блоки)</h2>
            <ul className="flex flex-col gap-2">{scenes.slice(0, 6).map((s) => (
              <li key={s.id} className="rounded border border-[#2a364b] bg-[#151c2b] p-2.5 text-sm">
                <p className="font-semibold">{s.title || "Без названия"}</p>
                <p className="mt-1 text-[#cfe0f5]">{s.summary}</p>
                {s.open_threads && <p className="mt-1 text-xs text-amber-300">Открыто: {s.open_threads}</p>}
              </li>))}</ul>
          </>)}
        </section>
      )}

      {tab === "atoms" && (
        <section className="flex flex-col gap-2">
          <div className="flex items-center gap-2 rounded border border-[#2a364b] bg-[#0d1320] px-2">
            <Search size={14} className="text-[#8d9ab0]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск по фактам" className="w-full bg-transparent py-1.5 text-sm outline-none" />
          </div>
          <ul className="flex flex-col gap-2">{atoms.map((a) => atomRow(a))}</ul>
          {atoms.length === 0 && <p className="text-sm text-[#8d9ab0]">Пока ничего нет. Факты появляются сами после разговоров.</p>}
        </section>
      )}

      {tab === "skills" && (
        <section className="flex flex-col gap-3">
          {ov?.is_admin && (
            <label className="flex items-center gap-2 text-sm text-[#9fb0c8]">
              <input type="checkbox" checked={!!ov.config.skills_auto} onChange={(e) => setAuto(e.target.checked)} />
              Принимать новые навыки автоматически (без моего одобрения)
            </label>
          )}
          {ov?.is_admin && pending.length > 0 && (<>
            <h2 className="text-sm font-bold text-amber-300">Ждут одобрения ({pending.length})</h2>
            <ul className="flex flex-col gap-2">{pending.map((a) => atomRow(a, true))}</ul>
          </>)}
          <h2 className="text-sm font-bold text-[#9fb0c8]">Одобренные навыки (опыт всей семьи, личного тут нет)</h2>
          <ul className="flex flex-col gap-2">{approved.map((a) => atomRow(a, true))}</ul>
          {approved.length === 0 && <p className="text-sm text-[#8d9ab0]">Пока нет одобренных навыков.</p>}
        </section>
      )}
    </div>
  )
}
